import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const HANDLE_PX = 22;

const timeAt = (event, duration, viewStart, viewSpan) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
  return Math.min(duration, Math.max(0, viewStart + ratio * viewSpan));
};

export default function VerseWaveform({
  peaks = [],
  duration = 0,
  viewStart = 0,
  viewSpan = 50,
  playhead = 0,
  startSec = null,
  endSec = null,
  onSeek,
  onSelection,
  onViewChange,
  onAutoSelect,
}) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const lastTapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return undefined;
    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.max(1, Math.floor(rect.width * dpr));
      const height = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#f3f6fb';
      ctx.fillRect(0, 0, width, height);

      const span = Math.max(viewSpan, 0.5);
      const toX = (sec) => ((sec - viewStart) / span) * width;
      if (startSec != null && endSec != null && endSec > startSec) {
        const x = toX(startSec);
        const w = Math.max(2, toX(endSec) - x);
        ctx.fillStyle = 'rgba(255, 152, 0, 0.28)';
        ctx.fillRect(x, 0, w, height);
      }

      const mid = height / 2;
      ctx.strokeStyle = '#3f51b5';
      ctx.lineWidth = Math.max(1, dpr);
      ctx.beginPath();
      const count = peaks.length;
      if (count > 0) {
        const secPerPeak = duration / count;
        let loudest = 0.0001;
        const first = Math.max(0, Math.floor(viewStart / secPerPeak));
        const last = Math.min(count - 1, Math.ceil((viewStart + span) / secPerPeak));
        for (let i = first; i <= last; i += 1) {
          if (peaks[i] > loudest) loudest = peaks[i];
        }
        for (let x = 0; x < width; x += 1) {
          const sec = viewStart + (x / width) * span;
          const index = Math.min(count - 1, Math.max(0, Math.floor(sec / secPerPeak)));
          const amp = Math.min(1, (peaks[index] || 0) / loudest);
          const bar = Math.max(1, amp * (height * 0.46));
          ctx.moveTo(x, mid - bar);
          ctx.lineTo(x, mid + bar);
        }
      }
      ctx.stroke();

      if (startSec != null) {
        const x = toX(startSec);
        ctx.fillStyle = '#ef6c00';
        ctx.fillRect(x - dpr, 0, Math.max(2, 2 * dpr), height);
      }
      if (endSec != null) {
        const x = toX(endSec);
        ctx.fillStyle = '#ef6c00';
        ctx.fillRect(x - dpr, 0, Math.max(2, 2 * dpr), height);
      }
      const playX = toX(playhead);
      ctx.fillStyle = '#c62828';
      ctx.fillRect(playX - dpr, 0, Math.max(2, 2 * dpr), height);
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [peaks, duration, viewStart, viewSpan, playhead, startSec, endSec]);

  const hitHandle = (event) => {
    if (startSec == null || endSec == null || !duration) return null;
    const rect = event.currentTarget.getBoundingClientRect();
    const span = Math.max(viewSpan, 0.5);
    const xStart = ((startSec - viewStart) / span) * rect.width;
    const xEnd = ((endSec - viewStart) / span) * rect.width;
    const x = event.clientX - rect.left;
    if (Math.abs(x - xStart) <= HANDLE_PX) return 'start';
    if (Math.abs(x - xEnd) <= HANDLE_PX) return 'end';
    if (x > xStart && x < xEnd) return 'move';
    return null;
  };

  const pointerDistance = () => {
    const points = [...pointersRef.current.values()];
    if (points.length < 2) return 0;
    return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
  };

  const onPointerDown = (event) => {
    if (!duration) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size >= 2) {
      dragRef.current = null;
      pinchRef.current = {
        dist: Math.max(pointerDistance(), 1),
        span: viewSpan,
        start: viewStart,
      };
      event.preventDefault();
      return;
    }
    const mode = hitHandle(event) || 'select';
    const at = timeAt(event, duration, viewStart, viewSpan);
    dragRef.current = {
      mode,
      origin: at,
      originX: event.clientX,
      start: startSec ?? at,
      end: endSec ?? at,
      moved: false,
    };
    event.preventDefault();
  };

  const onPointerMove = (event) => {
    if (pointersRef.current.has(event.pointerId)) {
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      const dist = Math.max(pointerDistance(), 1);
      const rect = event.currentTarget.getBoundingClientRect();
      const points = [...pointersRef.current.values()];
      const midX = (points[0].x + points[1].x) / 2;
      const ratio = rect.width ? (midX - rect.left) / rect.width : 0.5;
      const span = pinch.span * (pinch.dist / dist);
      const focus = pinch.start + ratio * pinch.span;
      onViewChange?.(focus - span * ratio, span);
      return;
    }
    const drag = dragRef.current;
    if (!drag || !duration) return;
    const at = timeAt(event, duration, viewStart, viewSpan);
    if (!drag.moved && Math.abs(event.clientX - drag.originX) < 6) return;
    drag.moved = true;
    if (drag.mode === 'start') {
      onSelection?.(Math.min(at, drag.end - 0.3), drag.end);
      return;
    }
    if (drag.mode === 'end') {
      onSelection?.(drag.start, Math.max(at, drag.start + 0.3));
      return;
    }
    if (drag.mode === 'move') {
      const width = Math.max(0.3, drag.end - drag.start);
      let nextStart = drag.start + (at - drag.origin);
      nextStart = Math.max(0, Math.min(duration - width, nextStart));
      onSelection?.(nextStart, nextStart + width);
      return;
    }
    const left = Math.min(drag.origin, at);
    const right = Math.max(drag.origin, at);
    onSelection?.(left, Math.max(right, left + 0.05));
  };

  const onPointerUp = (event) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    const drag = dragRef.current;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag || !duration || drag.moved) return;
    const now = Date.now();
    const last = lastTapRef.current;
    const doubleTap = last
      && now - last.time < 420
      && Math.abs(event.clientX - last.x) < 28;
    if (doubleTap) {
      lastTapRef.current = null;
      onAutoSelect?.(drag.origin);
      return;
    }
    lastTapRef.current = { time: now, x: event.clientX };
    onSeek?.(drag.origin);
  };

  return (
    <Box
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      sx={{
        width: '100%',
        height: { xs: 128, sm: 148 },
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'crosshair',
        touchAction: 'none',
        WebkitUserSelect: 'none',
        bgcolor: '#f3f6fb',
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }} />
    </Box>
  );
}
